import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { JointGiftingApprovalStatus } from '@interfaces';
import { Store } from '@ngrx/store';
import { FlowTypes } from '@shared/models';
import { Go } from '@store/actions';
import * as fromRootStore from '@store/index';
import { MXMApprovalStatus } from '@store/reducers/mxm/mxm.reducer';
import { selectJointGiftingApprovalStatus } from '@store/selectors/gifting/gifting.selectors';
import { selectMXMApprovalStatus } from '@store/selectors/mxm/mxm.selectors';
import { selectIsSubmitSuccessful } from '@store/selectors/submit/submit.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { CookieService } from 'ngx-cookie-service';
import { combineLatest, Observable, of, switchMap, take } from 'rxjs';

@Injectable()
export class SubmitGuard implements CanActivate {
  constructor(
    private globalStore: Store<fromRootStore.State>,
    private cookieService: CookieService,
  ) {}

  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    return combineLatest([
      this.globalStore.select(selectIsSubmitSuccessful),
      this.globalStore.select(selectMXMApprovalStatus),
      this.globalStore.select(selectJointGiftingApprovalStatus),
      this.globalStore.select(flowTypeSelection),
    ]).pipe(
      take(1),
      switchMap(([isSubmitSuccessful, mxmApprovalStatus, jointGiftingApprovalStatus, flowType]) => {
        const canAccess =
          isSubmitSuccessful ||
          mxmApprovalStatus === MXMApprovalStatus.SUCCESS ||
          jointGiftingApprovalStatus === JointGiftingApprovalStatus.SUCCESS;

        if (!canAccess) {
          const redirectPath = this.getRedirectPathByFlow(flowType);
          this.globalStore.dispatch(Go({ path: redirectPath.split('/').filter(Boolean) }));
          return of(false);
        }
        return of(true);
      }),
    );
  }

  private getRedirectPathByFlow(flowType: FlowTypes | null): string {
    switch (flowType) {
      case FlowTypes.GIFTING:
        return 'gifting/overview';

      case FlowTypes.MULTI_INHERITANCE:
        return 'inheritance/overview';

      case FlowTypes.INTERNAL_TRANSFER:
        return 'internal-transfer/overview';

      case FlowTypes.INHERITED_IRA:
        return 'inherited-ira/triage';

      case FlowTypes.MINOR:
        return 'minor-transfer/overview';

      case FlowTypes.DIVORCE:
        return 'internal-transfer/triage';

      case FlowTypes.INHERITANCE:
        return 'inheritance/overview';

      default:
        return 'inheritance/overview';
    }
  }
}
